import { MatrixClient } from 'matrix-js-sdk/src';

import logger from 'utils/logger';
import { calendarSyncLogger } from 'utils/logging/scopedLogger';
import { CustomMeedioMatrixEvents } from 'utils/matrixUtils';

import {
  ApiResponse,
  ERROR_MESSAGES,
  API_ENDPOINTS,
  PROVIDER_CONFIG,
  TimeRange,
  Event,
  Calendar,
  STORAGE_KEYS,
  AuthTokens,
  CalendarProvider,
} from './authHelpers';
import { storageUtils, dateUtils, errorUtils, transformUtils, RawEvent } from './utils';
import { CalendarType } from '../CalendarSyncDropdown/EditCalendarItem/CalendarRow';
import { MainCalendarInfo } from '../CalendarSyncDropdown/EditCalendarItem/useUpdateCalendars';

export const DEFAULT_CALENDAR_NAME = 'Meedio';
export const MICROSOFT_ROOM_ID_PROPERTY_FIELD = 'String {1b3b6503-2033-4d9a-8cc8-8c7cb6da7a48} Name roomId';

export const getIsCalendarAuthenticated = () => {
  const tokens = storageUtils.loadTokens();

  if (!tokens?.length) return false;

  return tokens.some((token) => !dateUtils.isTokenExpired(token));
};

export const isGoogleProvider = (provider: CalendarProvider): provider is CalendarProvider.GOOGLE =>
  provider === CalendarProvider.GOOGLE;
export const isMicrosoftProvider = (provider: CalendarProvider): provider is CalendarProvider.MICROSOFT =>
  provider === CalendarProvider.MICROSOFT;

export const getCalendars = async (provider: CalendarProvider, token: string): Promise<ApiResponse<Calendar[]>> => {
  try {
    const isGoogle = isGoogleProvider(provider);
    const endpoint = isGoogle ? API_ENDPOINTS.GOOGLE.CALENDAR_LIST : API_ENDPOINTS.MICROSOFT.CALENDAR_LIST;
    const response = await fetch(endpoint, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      return {
        data: [],
        error: errorUtils.handleApiError(response, `Failed to fetch calendars for ${provider}`),
        status: response.status,
      };
    }

    const key = isGoogle
      ? PROVIDER_CONFIG.GOOGLE.responseFields.calendars
      : PROVIDER_CONFIG.MICROSOFT.responseFields.calendars;

    const data = await response.json();
    const calendars = (data[key] || []).map((calendar: Record<string, unknown>) =>
      transformUtils.normalizeCalendar(calendar, provider)
    );

    return { data: calendars, status: 200 };
  } catch (error) {
    calendarSyncLogger.error('Failed to get calendars', { provider, error });
    return {
      data: [],
      error: errorUtils.createErrorMessage(error, `Failed to fetch calendars for ${provider}`),
      status: 500,
    };
  }
};

const removeToken = (token: string) => {
  const tokens = storageUtils.loadTokens();
  if (tokens.length) {
    const updatedTokens = tokens.filter(({ access_token }) => access_token !== token); // remove the invalid one
    storageUtils.set(STORAGE_KEYS.TOKENS, updatedTokens);
  }
};

export const getUserInfo = async (
  provider: CalendarProvider,
  token: string
): Promise<ApiResponse<{ email: string; id: string }>> => {
  try {
    const endpoint = isGoogleProvider(provider) ? API_ENDPOINTS.GOOGLE.USER_INFO : API_ENDPOINTS.MICROSOFT.USER_INFO;

    const response = await fetch(endpoint, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 401) removeToken(token);
      return {
        data: undefined,
        error: errorUtils.handleApiError(response, `Failed to fetch ${provider} user info`),
        status: response.status,
      };
    }

    const data = await response.json();

    // Normalize response structure between Google and Microsoft
    const userInfo = isGoogleProvider(provider)
      ? { email: data.email, id: data.id }
      : { email: data.mail || data.userPrincipalName, id: data.id };

    return { data: userInfo, status: 200 };
  } catch (error) {
    calendarSyncLogger.error('Failed to get user info', { provider, error });
    return {
      data: undefined,
      error: errorUtils.createErrorMessage(error, `Failed to fetch ${provider} user info`),
      status: 500,
    };
  }
};

export const getGoogleEvents = async (
  calendarId: string,
  timeRange: TimeRange,
  token: string
): Promise<ApiResponse<Event[]>> => {
  try {
    const tokens = storageUtils.loadTokens();
    if (!tokens.length) {
      return {
        data: [],
        error: ERROR_MESSAGES.AUTH.LOGIN_REQUIRED,
        status: 401,
      };
    }

    const url =
      `${API_ENDPOINTS.GOOGLE.EVENTS(calendarId)}` +
      `?timeMin=${encodeURIComponent(timeRange.start)}` +
      `&timeMax=${encodeURIComponent(timeRange.end)}` +
      '&singleEvents=true' +
      '&orderBy=startTime';

    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      return {
        data: [],
        error: errorUtils.handleApiError(response, 'Failed to fetch Google events'),
        status: response.status,
      };
    }

    const data = await response.json();
    const events = (data[PROVIDER_CONFIG.GOOGLE.responseFields.events] || []).map((event: RawEvent) => ({
      ...transformUtils.normalizeEvent(event, CalendarProvider.GOOGLE),
      calendarId,
    }));

    return { data: events, status: 200 };
  } catch (error) {
    calendarSyncLogger.error('Failed to get Google events', { calendarId, error });
    return {
      data: [],
      error: errorUtils.createErrorMessage(error, 'Failed to fetch Google events'),
      status: 500,
    };
  }
};

export interface GoogleCalendarResponse {
  etag: string;
  id: string;
  summary: string;
  description?: string;
  timeZone: string;
}

type AddNewCalendarProps = {
  provider: CalendarProvider;
  summary: string;
  description: string;
  token: string;
};

const prepareCalendarData = ({
  provider,
  summary,
  description,
}: Pick<AddNewCalendarProps, 'provider' | 'summary' | 'description'>) =>
  isGoogleProvider(provider)
    ? {
        summary,
        description,
        timeZone: 'UTC',
      }
    : { name: summary };

export const addNewCalendar = async ({
  provider,
  summary,
  description,
  token,
}: AddNewCalendarProps): Promise<ApiResponse<GoogleCalendarResponse>> => {
  try {
    // Prepare calendar data based on provider
    const calendarData = prepareCalendarData({
      provider,
      summary,
      description,
    });

    const endpoint = isGoogleProvider(provider) ? API_ENDPOINTS.GOOGLE.CALENDARS : API_ENDPOINTS.MICROSOFT.CALENDARS;

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(calendarData),
    });

    logger.info(`${provider} calendar creation response:`, response);

    if (!response.ok) {
      return {
        data: undefined,
        error: errorUtils.handleApiError(response, `Failed to create ${provider} calendar`),
        status: response.status,
      };
    }

    const data = await response.json();

    return { data, status: 200 };
  } catch (error) {
    calendarSyncLogger.error('Failed to add new calendar', { provider, summary, error });
    return {
      data: undefined,
      error: errorUtils.createErrorMessage(error, `Failed to create ${provider} calendar`),
      status: 500,
    };
  }
};

export const addNewEventToCalendar = async (
  provider: CalendarProvider,
  calendarId: string,
  event: Event,
  token: string,
  roomId: string
): Promise<ApiResponse<Event>> => {
  try {
    const isGoogle = isGoogleProvider(provider);
    const endpoint = isGoogle ? API_ENDPOINTS.GOOGLE.EVENTS(calendarId) : API_ENDPOINTS.MICROSOFT.EVENTS(calendarId);

    const customPayload = isGoogle
      ? { extendedProperties: { private: { roomId } } }
      : {
          singleValueExtendedProperties: [
            {
              id: MICROSOFT_ROOM_ID_PROPERTY_FIELD,
              value: roomId,
            },
          ],
        };

    const eventPayload = isGoogle
      ? {
          ...transformUtils.prepareEventForApi(event, provider),
          ...customPayload,
        }
      : {
          ...transformUtils.prepareEventForApi(event, provider),
          singleValueExtendedProperties: [
            {
              id: MICROSOFT_ROOM_ID_PROPERTY_FIELD,
              value: roomId,
            },
          ],
        };

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(eventPayload),
    });

    if (!response.ok) {
      return {
        data: undefined,
        error: errorUtils.handleApiError(response, `Failed to add new event to ${provider} Calendar`),
        status: response.status,
      };
    }

    const data = await response.json();
    return { data, status: 200 };
  } catch (error) {
    calendarSyncLogger.error('Failed to add new event to calendar', { provider, calendarId, roomId, error });
    return {
      data: undefined,
      error: errorUtils.createErrorMessage(error, `Failed to add new event to ${provider} Calendar`),
      status: 500,
    };
  }
};

export const deleteEventFromCalendar = async (
  provider: CalendarProvider,
  calendarId: string,
  eventId: string,
  token: string
): Promise<ApiResponse<null>> => {
  try {
    const encodedId = encodeURIComponent(eventId);
    const url = isGoogleProvider(provider)
      ? `${API_ENDPOINTS.GOOGLE.EVENTS(calendarId)}/${encodedId}`
      : `${API_ENDPOINTS.MICROSOFT.EVENTS(calendarId)}/${encodedId}`;

    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok && response.status !== 404) {
      return {
        data: null,
        error: errorUtils.handleApiError(response, `Failed to delete event from ${provider} Calendar`),
        status: response.status,
      };
    }

    return { data: null, status: 200 };
  } catch (error) {
    calendarSyncLogger.error('Failed to delete event from calendar', { provider, calendarId, eventId, error });
    return {
      data: null,
      error: errorUtils.createErrorMessage(error, `Failed to delete event from ${provider} Calendar`),
      status: 500,
    };
  }
};

export const removeCalendarEventsForRoom = async (
  matrixRoomId: string,
  accountCalendars: CalendarType[],
  mainCalendarInfo: MainCalendarInfo
): Promise<void> => {
  const { primaryCalendarId, token, isTokenExpired } = getPrimaryCalendarInformation(
    accountCalendars,
    mainCalendarInfo
  );
  if (!primaryCalendarId || !token || isTokenExpired) return;

  const provider = storageUtils.getProviderFromToken(token);
  const { data: events, error } = await getEventsByRoomId(
    provider,
    primaryCalendarId,
    matrixRoomId,
    token.access_token
  );

  if (error) {
    calendarSyncLogger.warn('removeCalendarEventsForRoom: getEventsByRoomId failed', {
      matrixRoomId,
      error,
    });
    return;
  }

  for (const event of events ?? []) {
    if (!event.id) continue;
    const { error: deleteError } = await deleteEventFromCalendar(
      provider,
      primaryCalendarId,
      event.id,
      token.access_token
    );
    if (deleteError) {
      calendarSyncLogger.warn('removeCalendarEventsForRoom: deleteEventFromCalendar failed', {
        matrixRoomId,
        eventId: event.id,
        error: deleteError,
      });
    }
  }
};

export const updateEventInCalendar = async (
  provider: CalendarProvider,
  calendarId: string,
  eventId: string,
  event: Partial<Event>,
  token: string
): Promise<ApiResponse<Event>> => {
  try {
    const encodedId = encodeURIComponent(eventId);
    const url =
      provider === 'google'
        ? `${API_ENDPOINTS.GOOGLE.EVENTS(calendarId)}/${encodedId}`
        : `${API_ENDPOINTS.MICROSOFT.EVENTS(calendarId)}/${encodedId}`;

    const originalEventResponse = await getEventById(provider, calendarId, eventId, token);
    if (!originalEventResponse.data) {
      return {
        data: undefined,
        error: originalEventResponse.error || 'Original event not found',
        status: originalEventResponse.status,
      };
    }

    const originalEvent = originalEventResponse.data;

    const preparedEvent = transformUtils.prepareEventForApi(
      { ...originalEvent, ...event, provider, calendarId },
      provider
    );

    const response = await fetch(url, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(preparedEvent),
    });

    if (!response.ok) {
      return {
        data: undefined,
        error: errorUtils.handleApiError(response, 'Failed to update event in Google Calendar'),
        status: response.status,
      };
    }

    const data = await response.json();
    return { data: transformUtils.normalizeEvent(data, provider), status: 200 };
  } catch (error) {
    calendarSyncLogger.error('Failed to update event in calendar', { provider, calendarId, eventId, error });
    return {
      data: undefined,
      error: errorUtils.createErrorMessage(error, 'Failed to update event in Google Calendar'),
      status: 500,
    };
  }
};

export const getEventsByRoomId = async (
  provider: CalendarProvider,
  calendarId: string,
  roomId: string,
  token: string
): Promise<ApiResponse<Event[]>> => {
  try {
    let url = '';
    const headers = {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };

    if (provider === 'google') {
      // Google: filter using privateExtendedProperty
      const params = new URLSearchParams({
        privateExtendedProperty: `roomId=${roomId}`,
      });
      url = `${API_ENDPOINTS.GOOGLE.EVENTS(calendarId)}?${params.toString()}`;
    } else if (provider === 'microsoft') {
      // Outlook: filter using singleValueExtendedProperties

      const filter = `singleValueExtendedProperties/any(ep: ep/id eq '${MICROSOFT_ROOM_ID_PROPERTY_FIELD}' and ep/value eq '${roomId}')`;

      // you can use /me/events or /me/calendars/{calendarId}/events
      url =
        `https://graph.microsoft.com/v1.0/me/calendars/${encodeURIComponent(
          calendarId
        )}/events?$filter=${encodeURIComponent(filter)}` +
        `&$expand=${encodeURIComponent(
          `singleValueExtendedProperties($filter=id eq '${MICROSOFT_ROOM_ID_PROPERTY_FIELD}')`
        )}`;
    } else {
      throw new Error(`Unsupported provider: ${provider}`);
    }

    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      return {
        data: undefined,
        error: errorUtils.handleApiError(response, `Failed to fetch events by roomId (${provider})`),
        status: response.status,
      };
    }

    const data = await response.json();
    const key = isGoogleProvider(provider) ? 'items' : 'value';
    const events = data[key].map((e: any) => transformUtils.normalizeEvent(e, provider));

    return { data: events, status: 200 };
  } catch (error) {
    calendarSyncLogger.error('Failed to get events by roomId', { provider, calendarId, roomId, error });
    return {
      data: undefined,
      error: errorUtils.createErrorMessage(error, `Failed to fetch events by roomId (${provider})`),
      status: 500,
    };
  }
};

export const getPrimaryCalendarInformation = (accountCalendars: CalendarType[], mainCalendarInfo: MainCalendarInfo) => {
  const primaryCalendarId = accountCalendars.find(
    (cal) => cal.parentId === mainCalendarInfo.id && cal.summary === DEFAULT_CALENDAR_NAME
  )?.id;
  const tokens = storageUtils.loadTokens();
  const token = tokens?.find(({ email }) => email === mainCalendarInfo.email);
  const isTokenExpired = dateUtils.isTokenExpired(token || ({} as AuthTokens));

  return { primaryCalendarId, token, isTokenExpired };
};

export const getEventById = async (
  provider: CalendarProvider,
  calendarId: string,
  eventId: string,
  token: string
): Promise<ApiResponse<Event>> => {
  try {
    const encodedId = encodeURIComponent(eventId);
    const url = isGoogleProvider(provider)
      ? `${API_ENDPOINTS.GOOGLE.EVENTS(calendarId)}/${encodedId}`
      : `${API_ENDPOINTS.MICROSOFT.EVENTS(calendarId)}/${encodedId}` +
        `?&$expand=${encodeURIComponent(
          `singleValueExtendedProperties($filter=id eq '${MICROSOFT_ROOM_ID_PROPERTY_FIELD}')`
        )}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      return {
        data: undefined,
        error: errorUtils.handleApiError(response, `Failed to fetch event from ${provider} Calendar`),
        status: response.status,
      };
    }

    const data = await response.json();
    const normalizedEvent = {
      ...transformUtils.normalizeEvent(data, provider),
      calendarId,
    };

    return { data: normalizedEvent, status: 200 };
  } catch (error) {
    calendarSyncLogger.error('Failed to get event by id', { provider, calendarId, eventId, error });
    return {
      data: undefined,
      error: errorUtils.createErrorMessage(error, `Failed to fetch event from ${provider} Calendar`),
      status: 500,
    };
  }
};

export const getMicrosoftEvents = async (
  calendarId: string,
  timeRange: TimeRange,
  token: string
): Promise<ApiResponse<Event[]>> => {
  try {
    const params = new URLSearchParams({
      startDateTime: timeRange.start,
      endDateTime: timeRange.end,
      $orderby: 'start/dateTime',
    });

    const url =
      `${API_ENDPOINTS.MICROSOFT.EVENTS(calendarId)}?${params.toString()}` +
      `&$expand=${encodeURIComponent(
        `singleValueExtendedProperties($filter=id eq '${MICROSOFT_ROOM_ID_PROPERTY_FIELD}')`
      )}`;

    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      return {
        data: [],
        error: errorUtils.handleApiError(response, 'Failed to fetch Microsoft events'),
        status: response.status,
      };
    }

    const data = await response.json();
    const events = (data[PROVIDER_CONFIG.MICROSOFT.responseFields.events] || []).map((event: RawEvent) => ({
      ...transformUtils.normalizeEvent(event, CalendarProvider.MICROSOFT),
      calendarId,
    }));

    return { data: events, status: 200 };
  } catch (error) {
    calendarSyncLogger.error('Failed to get Microsoft events', { calendarId, error });
    return {
      data: [],
      error: errorUtils.createErrorMessage(error, 'Failed to fetch Microsoft events'),
      status: 500,
    };
  }
};

export const getUniqCalendarItems = (items: CalendarType[]) =>
  items.filter((calendar, index, self) => index === self.findIndex(({ id }) => id === calendar.id));

export const getAccountCalendarsFromMatrix = (matrixClient?: MatrixClient | null): CalendarType[] =>
  (matrixClient?.getAccountData(CustomMeedioMatrixEvents.CALENDAR)?.getContent().calendars as CalendarType[]) || [];

export const getMainAccountDataFromMatrix = (matrixClient?: MatrixClient | null) =>
  (matrixClient?.getAccountData(CustomMeedioMatrixEvents.MAIN_CALENDAR_INFO)?.getContent().info as MainCalendarInfo) ||
  {};
